import { useMemo } from 'react'
import { z } from 'zod'
import { MMOverviewDateFilter } from '@/features/luxe-submissions/components/mm-overview-date-filter'
import { MMOverviewInventoryType } from '@/features/luxe-submissions/components/mm-overview-inventory-type'
import { MMOverviewLoading } from '@/features/luxe-submissions/components/mm-overview-loading'
import { MMOverviewPipelineBreakdown } from '@/features/luxe-submissions/components/mm-overview-pipeline-breakdown'
import { MMOverviewRecentActivity } from '@/features/luxe-submissions/components/mm-overview-recent-activity'
import { MMOverviewStalledAlert } from '@/features/luxe-submissions/components/mm-overview-stalled-alert'
import { MMOverviewStatCard } from '@/features/luxe-submissions/components/mm-overview-stat-card'
import { useGetMMDataOverview } from '@/features/luxe-submissions/queries/use-get-mm-data-overview'
import type {
	MMDataOverviewDatePreset,
	MMDataOverviewStatsItem
} from '@/features/luxe-submissions/types'
import { getDateRangeFromPreset } from '@/features/luxe-submissions/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import i18n from '@/translations/i18n'

export const MMDataOverviewSearchParamsSchema = z.object({
	preset: z.enum(['last_6_months', 'last_3_months', 'this_month']).optional(),
	dateFrom: z.string().optional(),
	dateTo: z.string().optional()
})

export const FILTER_PREVIOUS_LABEL: Record<
	MMDataOverviewDatePreset | 'custom_range',
	string
> = {
	last_6_months: i18n.t('common:previous_months', { count: 6 }),
	last_3_months: i18n.t('common:previous_months', { count: 3 }),
	this_month: i18n.t('common:last_month'),
	custom_range: i18n.t('common:previous_period')
}

export const OVERVIEW_CARD_CLASSNAME =
	'flex flex-col gap-4 rounded-4xl border border-neutral-300 p-4'

const computePercentChange = (current: number, previous: number) => {
	if (previous === 0) {
		return current > 0 ? 100 : 0
	}
	return Math.round(((current - previous) / previous) * 100)
}

function LuxeSubmissionsMMDataOverview() {
	const { searchParams } = useParsedSearchParams(
		MMDataOverviewSearchParamsSchema
	)

	const activePreset =
		searchParams.dateFrom && searchParams.dateTo
			? undefined
			: (searchParams.preset ?? 'this_month')

	const dateRange = useMemo(() => {
		if (searchParams.dateFrom && searchParams.dateTo) {
			return {
				dateFrom: searchParams.dateFrom,
				dateTo: searchParams.dateTo
			}
		}
		if (activePreset) {
			return getDateRangeFromPreset(activePreset)
		}
		return undefined
	}, [searchParams.dateFrom, searchParams.dateTo, activePreset])

	// Query
	const { data: overviewData, isLoading } = useGetMMDataOverview({
		startDate: dateRange?.dateFrom,
		endDate: dateRange?.dateTo
	})

	const stats = useMemo<MMDataOverviewStatsItem[]>(() => {
		if (!overviewData) {
			return []
		}
		const { needYourAction, deployed, avgDaysToDeploy, failedRejected } =
			overviewData.data
		return [
			{
				actionKeyName: 'need_your_action',
				count: needYourAction,
				changeValue: null
			},
			{
				actionKeyName: 'deployed',
				count: deployed.current,
				changeValue:
					deployed.previous === 0
						? null
						: computePercentChange(
								deployed.current,
								deployed.previous
							)
			},
			{
				actionKeyName: 'avg_days_to_deploy',
				count: avgDaysToDeploy.current,
				changeValue: avgDaysToDeploy.current - avgDaysToDeploy.previous
			},
			{
				actionKeyName: 'failed_rejected',
				count: failedRejected.current,
				changeValue: failedRejected.current - failedRejected.previous
			}
		]
	}, [overviewData])

	if (isLoading) {
		return <MMOverviewLoading />
	}

	return (
		<div className='flex flex-1 flex-col gap-6 overflow-auto px-10 py-6'>
			{overviewData?.data.stalledCount ? (
				<MMOverviewStalledAlert data={overviewData.data.stalledCount} />
			) : null}

			<MMOverviewDateFilter />

			<div className='grid grid-cols-4 gap-6'>
				{stats.map((card) => (
					<MMOverviewStatCard
						filter={activePreset ?? 'custom_range'}
						item={card}
						key={card.actionKeyName}
					/>
				))}
			</div>

			<div className='grid grid-cols-2 gap-6'>
				{overviewData?.data.pipelineBreakdown && (
					<MMOverviewPipelineBreakdown
						data={overviewData.data.pipelineBreakdown}
					/>
				)}
				{overviewData?.data.inventoryBreakdown && (
					<MMOverviewInventoryType
						data={overviewData.data.inventoryBreakdown}
					/>
				)}
				{overviewData?.data.recentActivity && (
					<MMOverviewRecentActivity
						data={overviewData.data.recentActivity}
						filter={activePreset ?? 'custom_range'}
					/>
				)}
			</div>
		</div>
	)
}

export { LuxeSubmissionsMMDataOverview }
